import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, startWith, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { ProtocolModel } from '../models/protocol.model';
import { protocolCreate } from '../store/protocol.actions';
import { ProtocolState } from '../store/protocol.reducers';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { SubCategoryModel } from '@features/admin/option-management/models/sub-category.model';
import { CategoryModel } from '../../admin/option-management/models/category.model';
import { ResearcherService } from '@features/admin/researcher-management/services/researcher.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';
import { displayResearcher as displayResearcherUtil, formatResearcher as formatResearcherUtil, formatPhone as formatPhoneUtil } from '@shared/researcher-format.util';


@Component({
  selector: 'app-protocol-add',
  templateUrl: './protocol-add.component.html',
  styleUrls: ['./protocol-add.component.scss']
})
export class ProtocolAddComponent implements OnInit {
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  form!: FormGroup;
  model = {} as ProtocolModel;
  filteredSponsors: { [index: number]: SubCategoryModel[] } = {};
  allSubCategories: SubCategoryModel[] = [];

  // Observables for dropdowns
  statusOptions$!: Observable<OptionModel[]>;

  // Autocomplete observables
  filteredPrincipalInvestigators$!: Observable<Researcher[]>;
  filteredNtrirbCoordinators$!: Observable<Researcher[]>;

  researchTypeOptions$!: Observable<OptionModel[]>;
  departmentOptions$!: Observable<OptionModel[]>;
  schoolOptions$!: Observable<OptionModel[]>;
  institutionOptions$!: Observable<OptionModel[]>;
  collaboratingInstitutionOptions$!: Observable<OptionModel[]>;
  sponsorTypeOptions$!: Observable<CategoryModel[]>;
  reviewTypeOptions$!: Observable<OptionModel[]>;
  reviewCycleOptions$!: Observable<OptionModel[]>;
  specialFindingOptions$!: Observable<SubCategoryModel[]>;
  fundingAgency$!: Observable<OptionModel[]>;
  natureOfResearch$!: Observable<OptionModel[]>;
  sponsoredClinicalTrial$!: Observable<OptionModel[]>;
  irbofRecords$!: Observable<OptionModel[]>;
  cededReviewTypeId?: number;
  sponsoredResearchTypeId?: number;
  showAutofillButton = false;
  personnelInstitutionOptions$!: Observable<OptionModel[]>;
  availableRoles$!: Observable<OptionModel[]>;
  subs$: Subscription[] = [];
  searchResearcher: { [index: number]: Researcher[] } = {};

  constructor(
    private fb: FormBuilder,
    private store: Store<ProtocolState>,
    private router: Router,
    private researcherService: ResearcherService
  ) { }

  ngOnInit(): void {
    const url = window.location.href;
    this.showAutofillButton = url.includes('localhost') || url.includes('-qa');

    this.model = {} as ProtocolModel;

    this.form = this.fb.group({
      studyTitle: ['', Validators.required],
      irbNetNumber: ['', [Validators.required, Validators.maxLength(250)]],
      protocolStatusId: [null],
      principalInvestigatorId: [null, Validators.required],
      investigatorSearch: ['', Validators.required],
      ntrIrb_CoordinatorId: [null],
      ntrIrbCoordinatorSearch: [''], // For autocomplete input
      typeOfResearchId: [null],
      departmentId: [null],
      schoolId: [null],
      institutionId: [null, Validators.required],
      collaboratingInstitutionId: [null],
      sponsorTypeCategories: this.fb.array([]),
      sponsor: ['', Validators.maxLength(250)],
      sponsorProtocol: ['', Validators.maxLength(250)],
      typeOfReviewId: [null],
      reviewCycleId: [null],
      specialFindingCategories: [[]],
      specialFindings: [''],
      submissionDate: [null, Validators.required],
      initialReview: [null],
      mostRecentReview: [null],
      initialApprovalDate: [null],
      approvalThrough: [null],
      sendNextReview: [null],
      continuingReviewDate: [null],
      completeDate: [null],
      irbofRecordId: [null],
      fundingAgencyId: [null],
      natureOfResearchId: [null],
      sponsoredClinicalTrialId: [null],
      personnelList: this.fb.array([]),
    });

    // Push one row on start
    this.addSponsorTypeCategory();

    this.store.pipe(select(optionSelector.sponsorSubCategories)).subscribe(subs => {
      this.allSubCategories = subs;
    });

    // Setup autocomplete for Principal Investigator
    this.filteredPrincipalInvestigators$ = this.form.get('investigatorSearch')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string') {
          return this.researcherService.searchResearchers(value, 'PrincipalInvestigator', true);
        }
        return of([]);
      })
    );

    // Setup autocomplete for NTRIRB Coordinator
    this.filteredNtrirbCoordinators$ = this.form.get('ntrIrbCoordinatorSearch')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string') {
          return this.researcherService.searchResearchers(value, 'NTRIRBCoordinator', true);
        }
        return of([]);
      })
    );

    // Dropdown options observables (with support for previously selected inactive values)
    this.statusOptions$ = this.optionStream(OptionType.protocolStatus, this.model.protocolStatusId);
    this.departmentOptions$ = this.optionStream(OptionType.department, this.model.departmentId);
    this.schoolOptions$ = this.optionStream(OptionType.school, this.model.schoolId);
    this.institutionOptions$ = this.optionStream(OptionType.institution, this.model.institutionId);
    this.collaboratingInstitutionOptions$ = this.optionStream(OptionType.collaboratingInstitution, this.model.collaboratingInstitutionId);

    this.sponsorTypeOptions$ = this.store.pipe(
      select(optionSelector.categories),
      map((categories: CategoryModel[]) => categories.filter(cat => cat.type?.toLowerCase() === 'sponsor' && cat.isActive))
    );

    this.fundingAgency$ = this.optionStream(OptionType.fundingAgency, this.model.fundingAgencyId);
    this.natureOfResearch$ = this.optionStream(OptionType.natureOfResearch, this.model.natureOfResearchId);
    this.sponsoredClinicalTrial$ = this.optionStream(OptionType.sponsoredClinicalTrial, this.model.sponsoredClinicalTrialId);
    this.irbofRecords$ = this.optionStream(OptionType.irbofRecord, this.model.irbofRecordId);
    this.reviewTypeOptions$ = this.optionStream(OptionType.typeOfReview, this.model.typeOfReviewId).pipe(
      map((options: OptionModel[]) => {
        const ceded = options.find(o => o.name.toLowerCase() === 'ceded');
        this.cededReviewTypeId = ceded?.id;
        return options;
      })
    );

    this.researchTypeOptions$ = this.optionStream(OptionType.typeOfResearch, this.model.typeOfResearchId).pipe(
      map(options => {
        const clinical = options.find(o => o.name.toLowerCase() === 'sponsored clinical trial (drug/device/biologic)'); //Sponsored Clinical Trial (Drug/Device/Biologic)
        this.sponsoredResearchTypeId = clinical?.id;
        return options;
      })
    );

    this.reviewCycleOptions$ = this.optionStream(OptionType.reviewCycle, this.model.reviewCycleId);
    this.specialFindingOptions$ = this.store.pipe(
      select(optionSelector.specialFindings),
      map((options: SubCategoryModel[]) => {
        const active = options.filter(o => o.isActive);
        const inactive = options.filter(o =>
          this.model.specialFindingCategories?.includes(o.id) && !o.isActive
        );
        return [...active, ...inactive].sort((a, b) => a.name.localeCompare(b.name));
      })
    );

    this.personnelInstitutionOptions$ = this.optionStream(OptionType.institution, this.model.personnelList?.filter(p => p.protocolId === this.model.id)[0]?.institutionId); // Get institutionId from first personnel entry if exists
    this.availableRoles$ = this.optionStream(OptionType.role, 0); // Get roleId from first personnel entry if exists

    // Conditional validators

    this.form.get('typeOfReviewId')?.valueChanges.subscribe((value) => {
      const irbofRecordControl = this.form.get('irbofRecordId');

      if (value === this.cededReviewTypeId) {
        irbofRecordControl?.setValidators([Validators.required]);
      } else {
        irbofRecordControl?.clearValidators();
        irbofRecordControl?.setValue('');
      }

      irbofRecordControl?.updateValueAndValidity();
    });

    this.form.get('typeOfResearchId')?.valueChanges.subscribe((value: number) => {
      const sponsoredClinicalTrialIdControl = this.form.get('sponsoredClinicalTrialId');

      if (value === this.sponsoredResearchTypeId) {
        sponsoredClinicalTrialIdControl?.setValidators([Validators.required]);
      } else {
        sponsoredClinicalTrialIdControl?.clearValidators();
        sponsoredClinicalTrialIdControl?.setValue('');
      }

      sponsoredClinicalTrialIdControl?.updateValueAndValidity();
    });
  }

  public displayResearcher = (r: Researcher | null) => displayResearcherUtil(r);
  public formatResearcher = (r: Researcher) => formatResearcherUtil(r);
  public formatPhone = (p: string) => formatPhoneUtil(p);

  // When researcher is selected from autocomplete
  onPrincipalInvestigatorSelected(researcher: Researcher): void {
    this.form.patchValue({ principalInvestigatorId: researcher.id, investigatorSearch: researcher });
  }

  onNtrirbCoordinatorSelected(researcher: Researcher): void {
    this.form.patchValue({ ntrIrb_CoordinatorId: researcher.id, ntrIrbCoordinatorSearch: researcher });
  }

  get sponsorTypeCategoriesForm(): FormArray {
    return this.form.get('sponsorTypeCategories') as FormArray;
  }

  get personnelArray(): FormArray {
    return this.form.get('personnelList') as FormArray;
  }

  newPersonnel(): FormGroup {
    return this.fb.group({
      researcherId: [null, Validators.required],
      rolesId: [[], [Validators.required]], // at least one checked
      institutionId: [null, Validators.required],
      startDate: [null, Validators.required],
      endDate: [null]
    });
  }

  addPersonnel() {
    const index = this.personnelArray.length;
    this.personnelArray.push(this.newPersonnel());
    this.ResearcherSearch(index);
  }

  removePersonnel(index: number) {
    this.personnelArray.removeAt(index);
  }

  addSponsorTypeCategory(): void {
    const index = this.sponsorTypeCategoriesForm.length;
    this.sponsorTypeCategoriesForm.push(
      this.fb.group({
        categoryId: [null, Validators.required],
        subCategoryId: [null, Validators.required],
        sponsorProtocol: ['']
      })
    );
    this.filteredSponsors[index] = [];
  }

  removeSponsorTypeCategory(index: number): void {
    this.sponsorTypeCategoriesForm.removeAt(index);
  }

  onSponsorTypeChange(index: number): void {
    const group = this.sponsorTypeCategoriesForm.at(index);
    const selectedTypeId = group.get('categoryId')?.value;

    if (!selectedTypeId) {
      group.get('subCategoryId')?.reset();
      this.filteredSponsors[index] = [];
      return;
    }

    this.filteredSponsors[index] = this.allSubCategories.filter(s => s.categoryId === selectedTypeId && s.isActive);
  }

  private optionStream(type: OptionType, selectedId?: number): Observable<OptionModel[]> {
    const id = selectedId ?? 0; // default to 0 if undefined
    return this.store.pipe(
      select(optionSelector.optionsByType(type, true)),
      map((options: OptionModel[]) => this.appendSelectedOptionIfDisabled(id, options))
    );
  }

  private appendSelectedOptionIfDisabled(id: number, options: OptionModel[]): OptionModel[] {
    const selectedOption = options.find(x => x.id === id);
    const activeOptions = options.filter(x => x.isActive === true);
    if (selectedOption && !selectedOption.isActive) {
      activeOptions.push(selectedOption);
    }
    return activeOptions.sort((a, b) => a.name.localeCompare(b.name));
  }

  save(): void {
    if (this.form.invalid) return;
    this.form.markAsPristine();
    const formValue = this.form.value;

    formValue.sponsorTypeCategories = formValue.sponsorTypeCategories.map((entry: any) => ({
      categoryId: entry.categoryId,
      subCategoryId: entry.subCategoryId,
      sponsorProtocol: entry.sponsorProtocol
    }));
    formValue.personnelList = formValue.personnelList.map((entry: any) => ({
      researcherId: entry.researcherId.id,
      rolesId: entry.rolesId,
      institutionId: entry.institutionId,
      startDate: entry.startDate,
      endDate: entry.endDate
    }));

    // Remove search fields before saving
    delete formValue.investigatorSearch;
    delete formValue.ntrIrbCoordinatorSearch;

    Object.keys(formValue).forEach(key => {
      if (formValue[key] === '') {
        formValue[key] = null;
      }
    });

    this.store.dispatch(protocolCreate({ data: { protocol: formValue } }));
  }

  cancel(): void {
    this.router.navigate(['protocol']);
  }

  fromEntries(entries: [string, any][]): Record<string, any> {
    return entries.reduce((acc, [key, val]) => {
      acc[key] = val;
      return acc;
    }, {} as Record<string, any>);
  }

  clearNtrIrbCoordinator() {
    this.form.patchValue({
      ntrIrbCoordinatorSearch: null,
      ntrIrb_CoordinatorId: null
    }, { emitEvent: false });
    this.form.get('ntrIrb_CoordinatorId')?.markAsDirty();
  }

  // For dynamic date field rendering in template
  dateFields = [
    { label: 'Submission Date', controlName: 'submissionDate' },
    { label: 'Initial Review Date', controlName: 'initialReview' },
    { label: 'Most Recent Review Date', controlName: 'mostRecentReview' },
    { label: 'Initial Approval Date', controlName: 'initialApprovalDate' },
    { label: 'Approval Through Date', controlName: 'approvalThrough' },
    { label: 'Send Next Review Date', controlName: 'sendNextReview' },
    { label: 'Continuing Review Date', controlName: 'continuingReviewDate' },
    { label: 'Complete Date', controlName: 'completeDate' }
  ];

  autofill(): void {
    combineLatest([
      this.statusOptions$,
      //this.investigatorOptions$,
      this.departmentOptions$,
      this.institutionOptions$,
      this.schoolOptions$,
      this.fundingAgency$,
      this.natureOfResearch$,
      this.researchTypeOptions$,
      this.reviewTypeOptions$,
      this.reviewCycleOptions$,
    ]).subscribe(([
      statuses,
      //investigators,
      departments,
      institutions,
      schools,
      funding,
      nature,
      researchTypes,
      reviewTypes,
      // reviewCycles
    ]) => {
      this.form.patchValue({
        studyTitle: 'Dummy Study Title',
        irbNetNumber: 'IRB12345',
        protocolStatusId: statuses[0]?.id ?? null,
        //investigatorId: investigators[0]?.id ?? null,
        departmentId: departments[0]?.id ?? null,
        schoolId: schools[0]?.id ?? null,
        institutionId: institutions[0]?.id ?? null,
        fundingAgencyId: funding[0]?.id ?? null,
        natureOfResearchId: nature[0]?.id ?? null,
        typeOfResearchId: researchTypes[0]?.id ?? null,
        typeOfReviewId: reviewTypes[0]?.id ?? null,
        // reviewCycleId: reviewCycles[0]?.id ?? null,
        specialFindings: 'None',
        specialFindingCategories: [],
        submissionDate: new Date(),
      });

      // Add a sponsor type row with valid values
      const categoryId = this.sponsorTypeOptions$ ? this.sponsorTypeOptions$ : null;
      const subCategory = this.allSubCategories.find(sc => !!sc.categoryId);

      if (subCategory) {
        const sponsorGroup = this.fb.group({
          categoryId: subCategory.categoryId,
          subCategoryId: subCategory.id,
          sponsorProtocol: 'Auto-protocol'
        });

        this.sponsorTypeCategoriesForm.clear();
        this.sponsorTypeCategoriesForm.push(sponsorGroup);

        // Populate the Sponsor dropdown
        this.onSponsorTypeChange(0);
        this.form.markAsDirty();
      }
    });
  }

  onStartDateChange(index: number): void {
    const personnelList = this.form.get('personnelList') as FormArray;
    const start = personnelList.at(index)?.get('startDate')?.value;
    const end = personnelList.at(index)?.get('endDate')?.value;

    let endDateControl = personnelList.at(index).get('endDate');
    if (start && end && new Date(start) > new Date(end)) {
      endDateControl?.setErrors({ dateRange: true });
    }
  }

  private ResearcherSearch(index: number): void {
    const personnelList = this.form.get('personnelList') as FormArray;
    const researcherIdControl = personnelList.at(index)?.get('researcherId') as FormControl;

    this.subs$.push(
      researcherIdControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value.length > 0) {
            return this.researcherService.searchResearchers(value, 'ResearchPersonnel', true).pipe(tap(results => this.searchResearcher[index] = results));
          }

          this.searchResearcher[index] = [];
          return [];
        }

        )
      ).subscribe()
    );
  }
}


